<template>
  <div class='title-div'>
    <!-- EventCopy - {{ eventCopy }}<br> -->
    <wc-page-title :title='calendar.name' :subtitle='subtitle'>
      <template v-slot:rightSide>
        <div>
          <v-btn icon :disabled='isLoading' @click="addNewEvent">
            <v-icon color='grey'>mdi-plus</v-icon>
          </v-btn>
          <v-btn icon @click="showHelpDialog = true">
            <v-icon color='grey'>mdi-help-circle-outline</v-icon>
          </v-btn>
        </div>
      </template>
    </wc-page-title>

    <v-container>
      <full-calendar :options="calendarOptions"></full-calendar>
    </v-container>

    <wc-dialog
      :showDialog='showEventDialog'
      :title='eventCopy.id ? "Edit Event Details" : "Create a new Event"'
      icon=''
      iconColor=''
      width='600'
      :disableButtons='!isFormValid || repeatingEventInstance'
      @cancel='closeEvent'
      @save='saveEvent'
    >
      <v-form :disabled='isLoading' v-model='isFormValid' ref='form'>
        <!-- {{ eventCopy }} -->
        <v-row dense>
          <v-col cols='12' md='6'>
            <v-text-field
              ref='titleInput'
              label='Title'
              v-model='eventCopy.title'
              prependIcon='mdi-label'
              :rules='[rules.required]'
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='6'> 
            <v-switch
              class='ml-8'
              label=' All-day'
              v-model='eventCopy.allDay'
            ></v-switch>
          </v-col>

          <v-col cols='12' md='6'>
            <wc-datepicker
              label='Starts'
              required
              v-model='eventCopy.tempStartDate'
              @change='autoAdjustEndDate'
            ></wc-datepicker>
          </v-col>
          <v-col cols='12' sm='6'>
            <wc-timepicker
              label='Start Time'
              increment='15'
              :start='dayStart'
              :end='dayEnd'
              :required='!eventCopy.allDay'
              prependIcon='mdi-timer-outline'
              :disabled='eventCopy.allDay'
              v-model='eventCopy.tempStartTime'
              @change='autoAdjustEndTime'
              :hint='eventCopy.allDay && eventCopy.tempStartTime ? "(will be deleted if saved as all-day)" : ""'
            ></wc-timepicker>
          </v-col>

          <v-col cols='12' md='6'>
            <wc-datepicker
              label='Ends'
              :min='eventCopy.tempStartDate'
              v-model='eventCopy.tempEndDate'
              @change='parseEndDateChange'
            ></wc-datepicker>
          </v-col>
          <v-col cols='12' sm='6'>
            <!-- <wc-timepicker
              label='End Time'
              increment='15'
              :start='dayStart'
              :end='dayEnd'
              prependIcon='mdi-timer-outline'
              :disabled='eventCopy.allDay'
              v-model='eventCopy.tempEndTime'
              :required='!eventCopy.allDay && !!eventCopy.tempEndDate'
              :hint='eventCopy.allDay && eventCopy.tempEndTime ? "(will be deleted if saved as all-day)" : ""'
              @change='parseEndTimeChange'
            ></wc-timepicker> -->

            <!-- there's v-model update issues with the above component, so I'm swapping to manual mode for the time being - MJ -->
            <!-- it only matters on the endTime, since endTime can auto-adjust based on a change to the start time/date -->
            <v-select
              ref='endTime'
              label='End Time'
              :menu-props='{auto: true}'
              prepend-icon='mdi-timer-outline'
              :disabled='eventCopy.allDay'
              :items='times'
              v-model='eventCopy.tempEndTime'
              :rules='!eventCopy.allDay && !!eventCopy.tempEndDate ? [rules.required] : []'
              :hint='eventCopy.allDay && eventCopy.tempEndTime ? "(will be deleted if saved as all-day)" : ""'
              persistent-hint
              @change='parseEndTimeChange'
            ></v-select>
          </v-col>
          <v-col cols='12' sm='6'>
            <v-select
              label='Repeat'
              :items='repeatOptions'
              prependIcon='mdi-calendar-sync'
              v-model='eventRepeat'
              :disabled='repeatingEventInstance'
              @change='parseRepeatChange'
            ></v-select>
          </v-col>
          <v-col cols='12' sm='6'>
            <v-expand-transition>
              <wc-datepicker
                v-if='eventRepeat'
                label='End Repeat'
                :min='eventCopy.tempStartDate'
                prependIcon='mdi-calendar-end'
                v-model='eventRepeatEnd'
                @change='parseRepeatEndChange'
                hint='this date is inclusive'
              ></wc-datepicker>
            </v-expand-transition>
          </v-col>
          <v-col cols='12'>
            <div v-if='eventCopy.exdate' class='py-0 my-0 caption grey--text'>
              Deleted repeating event instances:
              {{ formattedExdates }}
            </div>
          </v-col>
          <v-col cols='12'>
            Notes:
            <v-textarea rows='3' :auto-grow='$vuetify.breakpoint.mdAndUp' outlined v-model='eventCopy.details'></v-textarea>
          </v-col>

<!-- task area -->

          <v-col cols='12'>
            <wc-data-box
              v-if='eventCopy'
              mini
              :loading='isLoadingTasks'
              title='Related Tasks'
              :showAdd='!!eventCopy.id'
              @add='createTask'
              showHelp
              :helpText='taskHelpText'
            >
              <v-data-table
                dense
                :items='tasks'
                :headers='taskHeaders'
                hide-default-footer
                @click:row='openTask'
                :no-data-text='eventCopy.id ? "no tasks found for this event" : "event must be saved before tasks can be added"'
              >
                
                <template v-slot:item.status='{ item }'>
                  <v-tooltip top>
                    <template v-slot:activator='{ on }'>
                      <v-icon v-on='on' :color='getTaskStatus(item).color'>
                        {{ getTaskStatus(item).icon }}
                      </v-icon>
                    </template>
                    <span class='caption'>{{ getTaskStatus(item).tooltip }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.date_due='{ item }'>
                  <span v-if='item.date_due' :class='"caption " + calcDueColor(item)'>{{ item.date_due | shortDateWithDay }}
                    <span class='caption' v-if='item.time_due'>{{ item.time_due | shortTime }}</span>
                  </span>
                  <span v-else class="grey--text text--lighten-2 font-italic">not set</span>
                </template>

              </v-data-table>
            </wc-data-box>
          </v-col>

<!-- end task area -->
          
        </v-row>
      </v-form>
      <v-card-text v-if='timeError' class='caption error--text'>
        End Time is same/before Start Time.  Please correct before saving.
      </v-card-text>
      <v-card-text v-if='repeatingEventInstance' class='my-n6 caption error--text'>
        Future instances of repeating events cannot be edited... yet. (though they can be removed)
      </v-card-text>

      <template v-slot:dialog-actions>
        <v-tooltip top :disabled='!tasks.length' open-delay='500'>
          <template v-slot:activator='{ on }'>
            <div v-on='on' class='d-inline-block'>
              <v-btn v-if='eventCopy.id' text color='red' :disabled='!!tasks.length || isLoadingTasks' @click='showConfirmDelete = true'>Delete</v-btn>
            </div>
          </template>
          <span>You cannot delete an event that has tasks</span>
        </v-tooltip>

        <v-btn v-if='!isFormValid && !repeatingEventInstance' text small @click='checkForm'>Why can't I save?</v-btn>

      </template>

      <template v-slot:toolbarButtons>
        <span
          v-if='eventCopy.rrule && !eventCopy.rrule.count && !repeatingEventInstance'
          class='caption font-italic white--text'
        >
          repeating event parent
        </span>
      </template>
    </wc-dialog>

    <wc-dialog
      v-if='eventCopy.rrule'
      title='Please confirm'
      width='400'
      :showDialog='showConfirmDelete'
      :loadingButtons='isLoading'
      saveText='Delete'
      saveColor='error'
      :hideSave='!eventCopy.rrule.count && repeatingEventInstance'
      @cancel='showConfirmDelete = false'
      @save='deleteEvent'
    >
      <v-card-text>
        <p>Are you sure you want to delete this Event?</p>
        <p v-if='!eventCopy.rrule.count'>This is a repeating event.</p>
        <p v-if='!eventCopy.rrule.count && !repeatingEventInstance'><b>All</b> instances of this event will be deleted.</p>
        <p v-if='tasks.length'></p>
        <p v-if='!repeatingEventInstance' class='error--text font-weight-bold'>This cannot be undone!</p>
        <!-- Delete this Event Only  (hideSave) -->
        <v-btn v-if='!eventCopy.rrule.count && repeatingEventInstance' block class='error mt-2' small @click='deleteThisEventOnly'>
          Delete this event only
        </v-btn>
        <v-btn v-if='!eventCopy.rrule.count && repeatingEventInstance' block class='error mt-2' small @click='deleteEvent'>
          Delete all event instances
        </v-btn>
      </v-card-text>
    </wc-dialog>

    <wc-dialog
      :showDialog='showHelpDialog'
      title='Help'
      icon=''
      iconColor=''
      width='800'
      hideCancel
      saveText='Ok'
      @save='showHelpDialog = false'
    >
      <v-card-text v-html='helpText'></v-card-text>
    </wc-dialog>

    <task-dialog
      v-if='taskCopy'
      :loading='loading'
      :showDialog='showTaskDialog'
      :taskCopy.sync="taskCopy"
      :profiles='profiles'
      @save='saveTask'
      @cancel='closeTask'
      @deleteTask='deleteTask'
    ></task-dialog>

  </div>
</template>

<script>
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue'
import rrulePlugin from '@fullcalendar/rrule'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import scheduleAPI from '@/modules/schedule/api/schedule'
import scheduleServices from '@/modules/schedule/services/schedule'
import staffingAPI from '@/modules/staffing/api/staffing'
import TaskDialog from '@/modules/tasks/components/TaskDialog'
import tasksAPI from '@/modules/tasks/api/tasks'
import miscServices from '@/services/misc'
import formRules from '@/mixins/formRules'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'office-calendar',
  mixins: [formRules],
  props: {
    calendar: {
      required: true
    },
    loading: {
      required: true
    }
  },
  data() { return { // needed to change from arrow function so that this scoping works to move functions down to methods section.
    calendarOptions: {
      plugins: [ rrulePlugin, dayGridPlugin, interactionPlugin, timeGridPlugin ],
      // plugins: [ dayGridPlugin, interactionPlugin, timeGridPlugin ],
      initialView: this.$vuetify.breakpoint.smAndDown ? 'timeGridDay' : 'dayGridMonth',
      editable: true,
      selectable: true,
      navLinks: true,
      nowIndicator: true,
      selectMinDistance: 10,
      dayMaxEvents: true, // allow "more" link when too many events
      eventClick: this.eventClick,
      eventDrop: this.eventDrop,
      eventResize: this.eventResize,
      select: this.select,
      slotMinTime: "7:00:00",
      slotMaxTime: "18:00:00",
      expandRows: true,
      contentHeight: 640,
      weekNumbers: true,
      weekText: this.$vuetify.breakpoint.smAndDown ? "W" : "Week",
      views: {
        dayGridMonth: {
          weekNumberFormat: { week: 'short' }
        },
        timeGrid: {
          weekNumberFormat: { week: 'narrow' }
        },
        timeGridDay: {
          weekNumberFormat: { week: 'narrow' }
        }
      },
      eventDidMount: function(info) {
        if (info.event.extendedProps.details) {
          tippy(info.el, {
            content: info.event.extendedProps.details,
            placement: 'top',
            arrow: true,
            animation: 'scale'
          });
        }
      },
      headerToolbar: {
        // this will not seamlessly switch between views, but will load correctly for starting view (eg - on refresh)
        // moving this to a computed property doesn't work either, fullCal doesn't mesh perfectly with Vue's reactivity
        left: 'prev,next today',
        center: this.$vuetify.breakpoint.smAndDown ? '' : 'title',
        right: this.$vuetify.breakpoint.smAndDown ? '' :'dayGridMonth,timeGridWeek,timeGridDay'
      },
      events: [],
      // eventContent: this.eventContent
    },

    // ------ Regular Data ------
    subtitle: "",
    isFormValid: false,
    isLoading: false,
    showTaskDialog: false,
    showEventDialog: false,
    showConfirmDelete: false,
    eventCopy: {},
    resetEventCopy: {
      rrule: { freq: "weekly", count: "1" },
      allDay: false,
      tempStartDate: null,
      tempStartTime: null,
      tempEndDate: null,
      tempEndTime: null
    },
    showHelpDialog: false,
    eventTextColor: 'black',
    repeatOptions: [
      { text: "Never", value: null },
      { text: "Weekly", value: "weekly" },
      { text: "Monthly", value: "monthly" },
      { text: "Yearly", value: "yearly" },
    ],
    eventRepeat: null,  // putting this here to gain reactive control of a field that I don't need to pre-install on the event model - MJ
    eventRepeatEnd: null,
    repeatingEventInstance: false,
    dayStart: "07:00:00",
    dayEnd: "18:00:00",
    timeError: false,
    times: [],
    profiles: [],
    tasks: [],
    resetTask: {},
    taskCopy: null,
    isLoadingTasks: false,
    taskHeaders: [
      { text: '', value: 'status', width: "56", sortable: false },
      { text: 'Description', value: 'description' },
      { text: 'Assigned to', value: 'assigned_to_obj.name_display', width: "120" },
      { text: 'Due', value: 'date_due', width: "100" },
    ],
    // testing
    mdiClose: '<svg style="width:24px;height:24px" viewBox="0 0 24 24"><path fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" /></svg>',
    
    taskHelpText:"Please see the tasks module for more information",
    helpText:"<b>Welcome to the new Office Calendar.</b><br>"+
    "This Calendar is primarily designed for office reminders (at least for now)<br>"+
    "The current day is shaded in yellow, the current time indicated by a red line (week/day view only).<br><br>"+
    "<ul><li><b>Create a new event</b><br>"+
    "<ul><li>Use the plus button on the top right.<br>...or...</li>"+
    "<li>Click and drag across date(s)/times to create a new event with the specific range you've selected</li>"+
    "</ul></li><br>"+
    "<li><b>View/Edit an event</b><br>"+
    "<ul><li>Hovering over an event will display the notes.</li>"+
    "<li>Click on an event to open the details window.</li>"+
    "<li>You can drag and drop an event to change its date<br> (in week/day view, this will change its start time as well)</li>"+
    "<li>In month view, you can stretch the right side of an all-day event to extend the date range"+
    "<br><i>If you move/stretch a repeating event, all other instances of the event will update simultaneously.</i></li>"+
    "<li>In week and day view, you can also adjust the end time by grabbing the bottom of the event and stretching it downward</li>"+
    "<li>Dragging an event to/from the all-day area will change its type accordingly</li>"+
    "<li>Events without an end time default to appearance as if they're an hour long.</li>"+
    "</ul></li><br>" +
    "<li><b>More features coming soon</b></li>"+
    "<ul><li>Category color coding, Assign tasks, and additional repeating event enhancements.</li>"+
    "<li>Please note - Currently, if you want to change details to a repeating event, you have to find the original. Any change will update all event occurances.  There's no option (yet) to change details for <i>This Event Only</i> or <i>All future events</i> as you might on an iPhone calendar."+
    "</ul>"+
    "<br><br>We want to keep improving this calendar, so if you notice any feature behaving unexpectedly..."+
    "<br>Please report it (with as much detail as possible) to Westcap Solutions.  Thank you."
  }},
  computed: {
    formattedExdates() {
      if (this.eventCopy && this.eventCopy.exdate.length) {
        // let dates = ""
        // this.eventCopy.exdate.forEach( d => dates.concat(", " + this.$options.filters.shortDate(d)))
        return this.eventCopy.exdate
      }
      return ""
    }
  },
  mounted () {
    this.getData()
    this.times = miscServices.buildTimes(this.dayStart, this.dayEnd, 15)
  },
  methods: {
    async getData() {
      this.isLoading = true
      let tmp = await scheduleAPI.getEvents()
      // may need to do more processing here at some point - MJ
      let staff = await staffingAPI.getProfiles()
      this.profiles = _.orderBy(staff.filter(s => s.is_active === true), 'name_display')
      this.calendarOptions.events = tmp
      this.isLoading = false
    },
    select(info) {
      // click and drag event - which can be used to create a new event
      // console.log(info)
      // console.log('date(s) selected ' + info.startStr + ' to ' + info.endStr);
      this.tasks = []
      this.eventCopy = _.cloneDeep(this.resetEventCopy)
      // this.eventCopy = {}
      // this.eventCopy.rrule = { freq: "weekly", count: "1" },
      // if you simply reset the object (like above commented out) you lose reactivity.  So cloning a resetCopy works better.
      this.eventCopy.tempStartDate = info.startStr
      this.eventCopy.tempEndDate = info.endStr
      this.eventCopy.allDay = info.allDay
      if (!info.allDay) {
        this.eventCopy.tempStartTime = moment(info.startStr).format('HH:mm:ss').toString()
        this.eventCopy.tempEndTime = moment(info.endStr).format('HH:mm:ss').toString()
      } else {
        // fullCalendar likes setting the end to the next day at the first second.  I find this confusing, so I modify for UI - MJ
        this.eventCopy.tempEndDate = moment(info.endStr).subtract(1, 'days').format('YYYY-MM-DD').toString()
      }
      this.repeatingEventInstance = false
      this.showEventDialog = true
      this.$nextTick(() => {
        this.$refs.form.resetValidation()
      })
    },
    addNewEvent() {
      // console.log('Add new clicked')
      this.eventCopy = _.cloneDeep(this.resetEventCopy)
      this.tasks = []
      this.eventRepeat = null
      this.eventRepeatEnd = null
      this.repeatingEventInstance = false
      this.showEventDialog = true
      this.$nextTick(() => {
        this.$refs.form.resetValidation()
      })
    },
    checkForm () {
      this.$refs.form.validate()
    },
    eventClick ({ event }) {
      // console.log('----- EVENT CLICKED -----')
      // four params are available above { el, event, jsEvent, view }
      // console.log(el)
      // console.log(event)
      // console.log(jsEvent)
      // console.log(view)
      // console.log('def id: ', event._instance.defId)
      // console.log('instance id: ', event._instance.instanceId)
      // console.log('event id:', event.id)
      // console.log('Instance Start: ', moment.utc(event._instance.range.start).format("YYYY-MM-DD HH:mm:ss"))
      // console.log('Instance End: ', moment.utc(event._instance.range.end).format("YYYY-MM-DD HH:mm:ss"))
      // the event passed in by eventClick is read only, we need to find the original in order to make cleaner modifications (on a much simpler object).
      this.tasks = []
      this.eventCopy = _.cloneDeep(_.find(this.calendarOptions.events, e => e.id == event.id))
      this.getEventTasks()
      this.eventRepeat = _.find(this.repeatOptions, r => r.value == this.eventCopy.rrule.freq)
      if (this.eventCopy.rrule.count == 1) {
        this.eventRepeat = null 
      }
      this.eventRepeatEnd = this.eventCopy.rrule.until ? this.eventCopy.rrule.until : null
      // if 'until' is set at the same second or later as the dtstart, it will appear on the calendar. (same for repeats)
      
      // console.log('dtstart: ',this.eventCopy.rrule.dtstart)
      this.eventCopy.tempInstanceStart = moment.utc(event._instance.range.start).format("YYYY-MM-DD").toString()

      // adding this so that when the start is changed - the end can move with it (if editing manually) - though can change this to use duration

      this.eventCopy.tempStartDate = moment(event.start).format('YYYY-MM-DD').toString()
      if (!this.eventCopy.allDay) {
        this.eventCopy.tempStartTime = moment(event.start).format('HH:mm:ss').toString()
      } else {
        this.eventCopy.tempStartTime = null
      }
      if (event.end) {
        // console.log('eventCopy has an end date')
        if (!this.eventCopy.allDay) {
          // console.log('eventCopy is NOT all-day')
          this.eventCopy.tempEndDate = moment(event.end).format('YYYY-MM-DD').toString()
          this.eventCopy.tempEndTime = moment(event.end).format('HH:mm:ss').toString()
        } else {
          // console.log('eventCopy is all-day')
          // this is for user ease of use - see iPhone calendar functionality - we'll add this day back on save.
          // console.log('end before subtraction is', event.end)
          this.eventCopy.tempEndDate = moment(event.end).subtract(1, 'days').format('YYYY-MM-DD').toString()
          // console.log('eventCopy is all-day')
          // console.log('end date after subtraction is', this.eventCopy.tempEndDate)
          this.eventCopy.tempEndTime = null
        }
      } else {
        this.eventCopy.tempEndDate = null
        this.eventCopy.tempEndTime = null
      }

      // if repeating event, check to see if you're the original, or a future instance.
      // there may be an easier way to detect this using FC internal data, but not sure what that is yet - MJ
      if (moment.utc(event._instance.range.start).isSame(this.eventCopy.rrule.dtstart, 'day')) {
        this.repeatingEventInstance = false
      } else {
        this.repeatingEventInstance = true
      }
      this.showEventDialog = true
      this.$nextTick(() => {
        this.$refs.form.resetValidation()
      })
    },
    closeEvent() {
      this.showEventDialog = false
    },
    async saveEvent() {
      // console.log('save clicked')
      // this.isLoading = true
      if (this.eventCopy.tempStartDate && this.eventCopy.allDay) {
        this.eventCopy.start = moment(this.eventCopy.tempStartDate + ' 00:00:00', "YYYY-MM-DD HH:mm:ss").format().toString()
      } else if (this.eventCopy.tempStartDate && this.eventCopy.tempStartTime) {
        this.eventCopy.start = moment(this.eventCopy.tempStartDate + ' ' + this.eventCopy.tempStartTime, "YYYY-MM-DD HH:mm:ss").format().toString()
      } else {
        this.eventCopy.start = moment(this.eventCopy.tempStartDate + ' 09:00:00', "YYYY-MM-DD HH:mm:ss").format().toString()
      }

      if (!this.eventCopy.tempEndDate && this.eventCopy.allDay) {
        // if this is the case, we make the event 24hrs. (duration should always be 86,400,000)
        this.eventCopy.end = moment(this.eventCopy.tempStartDate).add(1, 'days')
        this.eventCopy.duration = moment(this.eventCopy.end).diff(moment(this.eventCopy.start))
      } else if (this.eventCopy.tempEndDate && this.eventCopy.allDay) {
        this.eventCopy.end = moment(this.eventCopy.tempEndDate + ' 00:00:00', "YYYY-MM-DD HH:mm:ss").add(1, 'days')
        this.eventCopy.duration = moment(this.eventCopy.end).diff(moment(this.eventCopy.start)) // mulitples of 86.4m
      } else if (this.eventCopy.tempEndDate && this.eventCopy.tempEndTime) {
        this.eventCopy.end = moment(this.eventCopy.tempEndDate + ' ' + this.eventCopy.tempEndTime, "YYYY-MM-DD HH:mm:ss")
        this.eventCopy.duration = moment(this.eventCopy.end).diff(moment(this.eventCopy.start))
      } else {
        this.eventCopy.end = null
      }
      // console.log('event Copy pre-service')
      // console.log(this.eventCopy)
      this.eventCopy = scheduleServices.getApiObj(this.eventCopy) // need to update the rrule field
      // console.log('sending eventCopy for save - obj below')
      // console.log(this.eventCopy)

      if (moment(this.eventCopy.end).isSameOrBefore(this.eventCopy.start) ) {
        this.timeError = true
      } else {
        if (this.eventCopy.id) {
          let tmp = await scheduleAPI.updateEvent(this.eventCopy)
          miscServices.replaceObject (this.calendarOptions.events, tmp)
        } else {
          let tmp = await scheduleAPI.createEvent(this.eventCopy)
          this.calendarOptions.events.push(tmp)
        }
        this.showEventDialog = false
        this.timeError = false
      }
      // this.isLoading = false
    },
    async eventDrop (obj) {
      // console.log('event DROP called - obj below')
      // console.log(obj)
      this.eventCopy = _.find(this.calendarOptions.events, e => e.id == obj.event.id)
      // console.log('eventCopy below')
      // console.log(this.eventCopy)
      // console.log(obj.event)
      // if a repeating event with exdates gets dragged, we need to make sure that the exdates get adjusted as well. - MJ
      let apiObj = scheduleServices.getApiObj(this.eventCopy, obj.event, obj.delta)
      // console.log('return from getAPIObj service below')
      // console.log(apiObj)
      let tmp = await scheduleAPI.updateEvent(apiObj)
      // console.log('return object below')
      // console.log(tmp)
      miscServices.replaceObject (this.calendarOptions.events, tmp)
    },
    async eventResize (eventResizeInfo) {
      // console.log('event RESIZE api call sending info')
      // console.log(eventResizeInfo)
      // I think I need to parse this into a duration
      // let apiObj = scheduleServices.getApiObj(obj.event)
      this.eventCopy = _.find(this.calendarOptions.events, e => e.id == eventResizeInfo.event.id)
      let apiObj = scheduleServices.getApiObj(this.eventCopy, eventResizeInfo.event)

      // console.log("what's actually sent below (from re-assignment service)")
      // console.log(apiObj)
      // console.log("eventCopy")
      // console.log(this.eventCopy)

      let tmp = await scheduleAPI.updateEvent(apiObj)

      // console.log('return object below')
      // console.log(tmp)
      miscServices.replaceObject (this.calendarOptions.events, tmp)
    },
    autoAdjustEndDate () {
      // console.log('adjust end called')
      if (this.eventCopy.tempEndDate && this.eventCopy.duration && this.eventCopy.tempStartDate) {
        this.$nextTick (() => {
          if (this.eventCopy.allDay) {
            this.$nextTick (() => {
              let tmp = moment(this.eventCopy.tempStartDate, 'YYYY-MM-DD').add(this.eventCopy.duration, 'milliseconds')
              this.eventCopy.tempEndDate = moment(tmp).subtract(1, 'days').format('YYYY-MM-DD').toString()
            })
          } else {
            this.$nextTick (() => {
              let tmp = moment(this.eventCopy.tempStartDate, 'YYYY-MM-DD').add(this.eventCopy.duration, 'milliseconds')
              this.eventCopy.tempEndDate = moment(tmp).format('YYYY-MM-DD').toString()
            })
          }
        })
      }

    },
    autoAdjustEndTime () {
      // console.log('end time adjusted')
      if (this.eventCopy.tempEndTime && this.eventCopy.duration) {
        this.$nextTick (() => {
          let tmp = moment(this.eventCopy.tempStartTime, 'HH:mm:ss').add(this.eventCopy.duration, 'milliseconds')
          this.eventCopy.tempEndTime = moment(tmp).format('HH:mm:ss').toString()
        })
      }
    },
    parseRepeatChange () {
      this.$nextTick(() => {
        if (this.eventRepeat && this.eventCopy.rrule.count) {
          // if there was a count, we need to delete it (to make it recurring)
          delete this.eventCopy.rrule.count
          this.eventCopy.rrule.freq = this.eventRepeat
        } else if (!this.eventRepeat) {
          // to make something with rrule NOT repeat, we need to set a count value to 1
          this.eventCopy.rrule.count = 1
          this.eventRepeatEnd = null
          if (this.eventCopy.rrule.until) {
            delete this.eventCopy.rrule.until
          }
          if (this.eventCopy.exdate) {
            this.eventCopy.exdate = null
          }
        } else {
          // if neither of those applies - there won't be a count, and the new Repeat will apply normally.
          // console.log('new repeat value selected: ', this.eventRepeat.value)
          this.eventCopy.rrule.freq = this.eventRepeat
          // MJError - the "repeating event parent" label in the toolbarbuttons slot acts awkwardly when these values are changed - later problem. 
        }
      })
    },
    parseRepeatEndChange () {
      // console.log('@parseRepeatEndChange')
      this.$nextTick(() => {
        if (this.eventRepeatEnd) {
          this.eventCopy.rrule.until = moment(this.eventRepeatEnd).format('YYYY-MM-DD').toString() + 'T' + moment(this.eventCopy.rrule.dtstart).format('HH:mm:ss').toString()
        } else {
          // console.log('deleting ---- until ----')
          delete this.eventCopy.rrule.until
        }
      })
    },
    parseEndTimeChange() {
      this.$nextTick(() => {
        if (this.eventCopy.tempEndTime && !this.eventCopy.tempEndDate) {
          this.eventCopy.tempEndDate = this.eventCopy.tempStartDate
        }
        // best to recalc duration here if changing multiple valiables at a time in the edit event dialog (not easy, can wait for now)
      })
    },
    parseEndDateChange() {
      this.$nextTick(() => {
        if (this.eventCopy.tempEndTime && !this.eventCopy.tempEndDate) {
          this.eventCopy.tempEndTime = null
        }
        // best to recalc duration here if changing multiple valiables at a time in the edit event dialog (not easy, can wait for now)
        this.$nextTick(() => {
          this.$refs.endTime.validate()
        })
      })
    },
    eventContent (arg) {
      // note - someday if want to implement this, need to solve non-all-day-event title overflow into adjacent dates.
      // console.log(arg)
      let el = document.createElement('div')
      if (arg.event.allDay) {
        el.innerHTML = arg.timeText + " " + arg.event.title
      } else if (arg.view.type == 'dayGridMonth') {
        el.innerHTML = '<svg height="10" width="10"><circle cx="5" cy="5" r="4" fill="' + arg.event.borderColor + '"/></svg>' + " " + arg.timeText + ' <b>' + arg.event.title + '</b>'
      } else {
        el.innerHTML = arg.timeText + '<br>' + arg.event.title
      }
      let arrayOfDomNodes = [ el ]
      return { domNodes: arrayOfDomNodes }
    },
    async deleteEvent() {
      this.isLoading = true
      await scheduleAPI.deleteEvent(this.eventCopy)
      miscServices.removeObject(this.calendarOptions.events, this.eventCopy)
      this.showConfirmDelete = false
      this.showEventDialog = false
      this.isLoading = false
    },
    async deleteThisEventOnly () {
      // console.log('delete this event only called - temp instance start should be in object below')
      // console.log(this.eventCopy)
      // console.log('instance start: ', this.eventCopy.tempInstanceStart)
      if (this.eventCopy.exdate) {
        this.eventCopy.exdate.push(this.eventCopy.tempInstanceStart)
      } else {
        this.eventCopy.exdate = [this.eventCopy.tempInstanceStart]
      }
      this.showConfirmDelete = false
      this.showEventDialog = false
      // need to add loading state here
      let updateObj = { id: this.eventCopy.id, exdate: this.eventCopy.exdate }
      let tmp = await scheduleAPI.updateEvent(updateObj)
      miscServices.replaceObject (this.calendarOptions.events, tmp)
    },
    async deleteAllFutureEvents () {
      // really, the user can change the repeat-until-date to accomplish the same thing 
      if (!this.repeatingEventInstance) {
        this.deleteEvent()
      } else {
        // set until the day before eventCopy.tempStartDate
        // need to go find and delete all exdates that are later than the new end date (to be complete...
        // ... or else they'll stay connected to the event, and if it's expanded again, the user may not get the repeats they expect.)
      }
    },

    // to be implemented when tasks become a thing.
    saveForAllFutureEvents () {
      console.log('save --ALL future events-- called')
      // need to check if this is first instance, if so, make changes like normal,
      // if not - set until to the current instance date and save the old event (stripped of any potentially new data).
      // post a new event with the changed info - (strip id and be sure to update dtstart to new instance date) 

    },
    saveForThisEventOnly() {
      console.log('save for --this event only-- called')
      // create an eventCopy2
      // add an exdate to eventCopy2 by calling deleteThisEventOnly
      // 
      // save eventCopy as a new event (strip it's id and make a post - be sure to remove repeat and repeat end)
      // maybe give the user the option to keep group id or not? - it can stay with the repeat, hmm, this will occur only on drag
      // maybe the groupId can be found and all events in the group could be updated. (this might be the only way to keep actualy repeating events, that have differing tasks together... though, this might also be a longshot to figure out) 
      
    },

// ----- TASKS -----

    getTaskPriority(item) {
      let tmp = this.$store.state.misc.globalOptions.taskPriorities.find(p => p.value === item.priority)
      if (tmp) {
        return tmp
      }
      return { text: "", value: null }
    },
    getTaskStatus (item) {
      if (!item.id) {
        return this.$icons.task_new
      } else if (!item.read) {
        return this.$icons.task_unread
      } else if (item.is_complete) {
        return this.$icons.task_complete
      } else if (item.is_attempted) {
        return this.$icons.task_attempted
      } else if (item.in_progress) {
        return this.$icons.task_progress
      } else {
        return this.$icons.task_read
      }
    },
    calcDueColor(item) {
      if ( item.time_due && moment(item.date_due + 'T' + item.time_due).isBefore(this.now) ) {
        return "error--text"
      } else if ( moment(item.date_due).isSame(this.now, 'day') ) {
        return "warning--text"
      } else if ( moment(item.date_due).isBefore(this.now, 'day') ) {
        return "error--text"
      } else {
        return ""
      }
    },
    async getEventTasks () {
      this.isLoadingTasks = true
      this.tasks = await tasksAPI.getEventTasks(this.eventCopy.id)
      this.isLoadingTasks = false
    },
    createTask() {
      this.taskCopy = this.resetTask
      this.taskProgress = null
      this.taskCopy.created_by = this.$store.state.auth.profile.id
      this.taskCopy.date_created = moment().format('YYYY-MM-DDTHH:mm:ss')
      this.taskCopy.event = this.eventCopy.id
      this.taskCopy.description = this.eventCopy.details
      if (this.eventCopy.tempInstanceStart) {
        this.taskCopy.date_due = this.eventCopy.tempInstanceStart
      }
      this.showTaskDialog = true
    },
    openTask(task) {
      this.taskCopy = _.cloneDeep(task)
      this.$nextTick(() => {
        this.showTaskDialog = true
      })
    },
    async saveTask(){
      this.isLoadingTasks = true
      if (this.taskCopy.id) {
        let tmp = await tasksAPI.updateTask(this.taskCopy)
        miscServices.replaceObject(this.tasks, tmp)
      } else {
        let tmp = await tasksAPI.createTask(this.taskCopy)
        this.tasks.push(tmp)
      }
      this.showTaskDialog = false
      this.isLoadingTasks = false
    },
    closeTask () {
      this.showTaskDialog = false
    },
    async deleteTask () {
      this.isLoadingTasks = true
      await tasksAPI.deleteTask(this.taskCopy.id)
      miscServices.removeObject(this.tasks, this.taskCopy)
      this.showTaskDialog = false
      this.isLoadingTasks = false
    }
  },
  components: {
    'full-calendar': FullCalendar,
    'task-dialog': TaskDialog,
  }
}
</script>

<style>
  
.tippy-content {
  font-family: Roboto;
}

</style>

